// U+2068 FSI (First Strong Isolate) and U+2069 PDI (Pop Directional Isolate).
// WhatsApp wraps contact names in these Unicode direction markers when copying a mention.
const WA_MENTION_RE = new RegExp("[-–]?\\s*@⁨([^⁩]+)⁩\\s*(\\+\\s*)?", "u");
const SIMPLE_MENTION_RE = /[-–]?\s*@\s*([\p{L}\p{M}\d'"׳״\-]+)\s*(\+\s*)?/u;
// Use Extended_Pictographic (not Emoji) to avoid matching *, #, digits which
// are valid emoji bases but should be treated as bullets/text in our context.
const EMOJI_START = /^\p{Extended_Pictographic}/u;
// Bullet: optional leading whitespace, then *, -, or •, then at least one space.
const BULLET_LINE = /^[ \t]*[-*•][ \t]+/;
function genId() {
    return crypto.randomUUID();
}
export function parseItemText(raw) {
    let text = raw.trim();
    let quantity;
    let assignedTo;
    let note;
    // Step 1: Extract assignment first (removes the token from text).
    const waMatch = text.match(WA_MENTION_RE);
    if (waMatch) {
        assignedTo = waMatch[1].trim();
        text = text.replace(waMatch[0], "").trim();
    }
    else {
        const simpleMatch = text.match(SIMPLE_MENTION_RE);
        if (simpleMatch) {
            assignedTo = simpleMatch[1].trim();
            text = text.replace(simpleMatch[0], "").trim();
        }
    }
    // Step 2: Extract quantity patterns (in priority order).
    // Must run AFTER assignment removal so assignment tokens don't confuse quantity parsing.
    const QUANTITY_PATTERNS = [
        // (כ-32) or (כ-2)
        /\s*\(כ[־\-]\d+(?:[–\-]\d+)?\)/u,
        // ×2 or x2 / X2
        /\s+[×x]\d+(?:[–\-]\d+)?/iu,
        // כ-32 standalone (no parens)
        /\s+כ[־\-]\d+(?:[–\-]\d+)?/u,
        // 5 יח׳ or 5 יח'
        /\s+\d+\s*יח[׳']/u,
        // Range like 3–5 or 3-5 at end
        /\s+\d+[–\-]\d+\s*$/u,
        // Bare trailing integer
        /\s+\d+\s*$/u,
    ];
    for (const pat of QUANTITY_PATTERNS) {
        const m = text.match(pat);
        if (m) {
            // Strip parens if present
            quantity = m[0].trim().replace(/^\(|\)$/g, "");
            text = text.slice(0, text.length - m[0].length).trim();
            break;
        }
    }
    // Step 3: If a leading digit-text fragment remains (e.g. "2 גולדסטאר, 2 קרלסברג"),
    // save as note to avoid data loss.
    if (!note && /^\d+\s+\S/.test(text)) {
        const m = text.match(/^(\d+)\s+(\S.*)/u);
        if (m) {
            // Only treat as note when remaining text has a comma (multi-item list).
            if (m[2].includes(",")) {
                note = text;
                text = "";
            }
        }
    }
    return {
        text,
        ...(quantity ? { quantity } : {}),
        ...(assignedTo ? { assignedTo } : {}),
        ...(note ? { note } : {}),
    };
}
export function parseTextToPlanContent(raw) {
    const lines = raw.split("\n");
    const sections = [];
    let currentSection = null;
    let currentSubsection = null;
    let seenFirstSection = false; // true once we've seen the first emoji section
    function flushSubsection() {
        if (currentSubsection && currentSection && currentSubsection.items.length > 0) {
            currentSection.subsections.push(currentSubsection);
        }
        currentSubsection = null;
    }
    function flushSection() {
        flushSubsection();
        if (currentSection) {
            sections.push(currentSection);
        }
        currentSection = null;
    }
    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line)
            continue;
        // Check bullets BEFORE emoji (since * matches \p{Emoji}).
        // Bullet item: *, -, •
        if (BULLET_LINE.test(line)) {
            if (!seenFirstSection) {
                // Bullet before any emoji section → create implicit unnamed section
                seenFirstSection = true;
                currentSection = { id: genId(), title: "", subsections: [] };
                // Will be pushed by flushSection()
            }
            if (!currentSection) {
                currentSection = { id: genId(), title: "", subsections: [] };
            }
            if (!currentSubsection) {
                currentSubsection = { id: genId(), items: [] };
            }
            const itemRaw = line.replace(BULLET_LINE, "").trim();
            const parsed = parseItemText(itemRaw);
            currentSubsection.items.push({
                id: genId(),
                checked: false,
                ...parsed,
            });
            continue;
        }
        // Emoji line → new top-level section
        if (EMOJI_START.test(line)) {
            seenFirstSection = true;
            flushSection();
            const iconMatch = line.match(/^(\p{Extended_Pictographic}[︎️]?)\s*/u);
            const icon = iconMatch?.[1]?.trim() ?? undefined;
            const title = icon ? line.slice(icon.length).trim() : line;
            currentSection = { id: genId(), title, icon, subsections: [] };
            continue;
        }
        // Non-emoji, non-bullet text line
        if (!seenFirstSection) {
            // Preamble (title lines before first section) — skip entirely
            continue;
        }
        // Subsection header under current section
        flushSubsection();
        if (!currentSection) {
            // Orphan subsection header with no section — make it a section
            currentSection = { id: genId(), title: line, subsections: [] };
        }
        else {
            currentSubsection = { id: genId(), title: line, items: [] };
        }
    }
    flushSection();
    return { sections };
}
